/**
 * useFollowUpHistory
 * Carrega o histórico de jobs de follow-up (contagens por status + lista
 * filtrada por status/data) para a aba "Histórico".
 */
import { useCallback, useEffect, useState } from 'react';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { notify } from '../../../../utils/toast';
import { buildDateTimeIso } from '../../../../components/ui/DateTimeField';
import * as followUpService from '../../../../services/modules/followUpService';
import type { FollowUpJob } from '../types';

export type FollowUpHistoryStatus = 'scheduled' | 'sent' | 'failed' | 'paused';

export type FollowUpHistoryCounts = Record<FollowUpHistoryStatus, number>;

const ZERO_COUNTS: FollowUpHistoryCounts = { scheduled: 0, sent: 0, failed: 0, paused: 0 };

export function useFollowUpHistory(options?: { includeRows?: boolean }) {
    const includeRows = options?.includeRows !== false;
    const { currentOrganization } = useOrganization();
    const organizationId = currentOrganization?.id;

    const [status, setStatus] = useState<FollowUpHistoryStatus>('scheduled');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [counts, setCounts] = useState<FollowUpHistoryCounts>(ZERO_COUNTS);
    const [rows, setRows] = useState<FollowUpJob[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchCounts = useCallback(
        async (filterStartDate: string, filterEndDate: string) => {
            if (!organizationId) {
                setCounts(ZERO_COUNTS);
                return;
            }
            try {
                const data = await followUpService.countJobsByStatus(organizationId, {
                    startDate: filterStartDate
                        ? buildDateTimeIso(filterStartDate, undefined, 0, 0)
                        : undefined,
                    endDate: filterEndDate
                        ? buildDateTimeIso(filterEndDate, undefined, 23, 59)
                        : undefined,
                });
                setCounts({
                    scheduled: data.scheduled,
                    sent: data.sent,
                    failed: data.failed,
                    paused: data.paused,
                });
            } catch (error) {
                console.error('[useFollowUpHistory] Erro ao contar jobs de follow-up:', error);
            }
        },
        [organizationId]
    );

    const fetchRows = useCallback(
        async (targetStatus: FollowUpHistoryStatus, filterStartDate: string, filterEndDate: string) => {
            if (!organizationId) {
                setRows([]);
                return;
            }

            setLoading(true);
            try {
                const data = await followUpService.listJobs(organizationId, {
                    status: targetStatus,
                    startDate: filterStartDate ? buildDateTimeIso(filterStartDate, undefined, 0, 0) : undefined,
                    endDate: filterEndDate ? buildDateTimeIso(filterEndDate, undefined, 23, 59) : undefined,
                });
                setRows(data ?? []);
            } catch (error) {
                console.error('[useFollowUpHistory] Erro ao carregar histórico de follow-ups:', error);
                notify.error('Erro ao carregar o histórico de follow-ups.');
                setRows([]);
            } finally {
                setLoading(false);
            }
        },
        [organizationId]
    );

    // Recarrega contagens + linhas ao trocar organização ou aba de status,
    // sempre aplicando o filtro de data vigente nos inputs.
    useEffect(() => {
        fetchCounts(startDate, endDate);
        if (includeRows) {
            fetchRows(status, startDate, endDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, status, includeRows]);

    const refresh = useCallback(() => {
        fetchCounts(startDate, endDate);
        fetchRows(status, startDate, endDate);
    }, [fetchCounts, fetchRows, status, startDate, endDate]);

    return {
        status,
        setStatus,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        counts,
        rows,
        loading,
        refresh,
    };
}
